import {IBannerData, BannerDataNull} from 'OK/banners/bannerNew/data/BannerDataNull';
import {BannerDataIframeNull} from 'OK/banners/bannerNew/data/BannerDataIframeNull';
import {BannerDataImage} from 'OK/banners/bannerNew/data/BannerDataImage';
import {BannerDataText} from 'OK/banners/bannerNew/data/BannerDataText';
import {HtmlRendererJs} from 'OK/banners/bannerNew/utils/HtmlRendererJs';
import {DispatcherJs} from 'OK/DispatcherJs';
import {ValueProviderJs} from "OK/banners/bannerNew/ValueProviderJs";
import {SimpleBannerJs} from "OK/banners/bannerNew/banners/SimpleBannerJs";
import {BanDataCfgJs} from "OK/BanDataCfgJs";
import {detect as detectWhiteList} from "OK/wld";
import * as logger from 'OK/logger';
import {BannerDataIframe} from "OK/banners/bannerNew/data/BannerDataIframe";

declare global {
    interface Window {
        trg_gc: () => void;
        rb_js_bannerClick: (id: string) => void;
    }
}

const CLICK_PARAM_PREFIX = "nbnr_";
const registry: Record<string, BannerViewJs | null> = {};

let next_id = 0;
let renderer: HtmlRendererJs | null = null;

function invokeTrgGc() {
    if (typeof window.trg_gc === 'function') {
        window.trg_gc();
    }
}

function getRenderer(): HtmlRendererJs {
    if (renderer === null) {
        renderer = new HtmlRendererJs();
    }
    return renderer;
}

function handleBannerClick(id: string) {
    const uid = id.slice(CLICK_PARAM_PREFIX.length);
    const view = registry[uid];
    if (view && view.banner !== null) {
        view.banner.handleBannerClick();
    }
}

const THRESHOLD = 1000 * 40;
let userActive = Date.now();

function userAction() {
    userActive = Date.now();
}

window.addEventListener('scroll', userAction);
document.body.addEventListener('mousemove', userAction);

function isUserActive() {
    return (Date.now() - userActive) < THRESHOLD;
}

let whiteListDetected = true;

detectWhiteList().catch(function() {
    whiteListDetected = false;
});

// ======================================================
// =================== BannerViewJs =====================
// ======================================================

interface IBannerViewOptions {
    container: HTMLElement | null;
    slot: ValueProviderJs<number> | null;
    adfTransport: boolean;
    inline?: boolean;
    onRender?: (banner: IBannerData) => void;
    onError?: (banner: IBannerData) => void;
}

const defaultOptions: IBannerViewOptions = {
    container: null,
    slot: null,
    adfTransport: false,
    inline: false,
    onRender: undefined,
    onError: undefined
};

class BannerViewJs {
    options: IBannerViewOptions;
    container: HTMLElement;
    uid: string;
    slot: number;
    place: string | null;
    adfTransport = false;
    nullBannerData: IBannerData;

    heightChangeWatcher: HeightChangeWatcherJs;
    nullBannerRenderer: NullBannerRendererJs;
    textBannerRenderer: TextBannerRendererJs;
    imageBannerRenderer: ImageBannerRendererImplJs;
    iframeNullBannerRenderer: IframeNullBannerRendererJs;
    iframeBannerRenderer: IframeBannerRendererJs;
    contentChangeObserver: MutationObserver | null = null;
    banner: SimpleBannerJs | null = null;

    constructor(opts: IBannerViewOptions) {
        const options = {...defaultOptions, ...opts};
        this.options = options;
        this.container = options.container!;
        this.uid = '' + (next_id++);
        this.slot = options.slot?.getValue()!;
        this.place = this.container ? this.container.getAttribute('data-place') : null;
        this.adfTransport = options.adfTransport;
        this.nullBannerData = this.adfTransport ? BannerDataIframeNull.IFRAME_NULL : BannerDataNull.NULL;

        if (this.slot !== null) {
            getRenderer().setBannerPlace(options.container!, this.slot);
        }

        this.heightChangeWatcher = new HeightChangeWatcherJs(this);
        this.nullBannerRenderer = new NullBannerRendererJs(this);
        this.textBannerRenderer = new TextBannerRendererJs(this);
        this.imageBannerRenderer = new ImageBannerRendererImplJs(this);
        this.iframeNullBannerRenderer = new IframeNullBannerRendererJs(this);
        this.iframeBannerRenderer = new IframeBannerRendererJs(this);
    }

    /**
     * true for banners in main document (not in poplayers)
     */
    isInline(): boolean {
        return this.options.inline!;
    }

    attach(banner: SimpleBannerJs) {
        registry[this.uid] = this;
        this.banner = banner;

        this.heightChangeWatcher.start();
        this.createBannerDomObserver(this.container);
    }

    detach() {
        this.destroyBannerDomObserver();
        this.heightChangeWatcher.stop();

        this.renderNullBanner();
        this.banner = null;
        registry[this.uid] = null;
    }

    public isInViewport(checkLayers: boolean): boolean {

        try {
            if (!isUserActive()) {
                return false;
            }

            if (whiteListDetected) {
                return false;
            }

            if (document.visibilityState === 'hidden') {
                return false;
            }

            if (checkLayers && window.OK.Layers.isAnyLayerOpened()) {
                return false;
            }

            const el = this.container;
            const threshold = BanDataCfgJs.getInstance().checkInViewportThreshold;
            const screenTop = window.pageYOffset;
            const screenHeight = window.innerHeight;
            const rect = el.getBoundingClientRect();
            const elPart = rect.height * threshold;
            const elTop = rect.top + screenTop;
            const elBottom = rect.bottom + screenTop;

            if (screenHeight < elPart) {
                return false;
            }

            const lowerBound = screenTop;
            const upperBound = screenHeight + screenTop;

            return ((elTop + elPart) < upperBound && elTop > lowerBound) ||
                ((elBottom - elPart) > lowerBound && elBottom < upperBound) ||
                (elTop < lowerBound && elBottom > upperBound);

        } catch (e) {
            logger.errorEx(e as Error, "BannerViewJs", "isInViewport");
            return false;
        }
    }

    getContainer(): HTMLElement {
        return this.container;
    }

    getSlot(): number {
        return this.slot;
    }

    getPlace(): string {
        return this.place!;
    }

    isAdfTransport(): boolean {
        return this.adfTransport;
    }

    showContainer() {
        this.container.style.display = 'block';
        // Не показываем пользователю контент в момент обновления
        this.container.style.visibility = 'hidden';
        // Через 100 мс показываем контейнер банера. Полагаем, что за это время все перерасчёты (привет, липучка!) будут выполнены
        // todo: wtf magic 100ms ?
        setTimeout(() => {
            if (this.container !== null) {
                this.container.style.visibility = 'visible';
            }
        }, 100);
    }

    hideContainer() {
        this.container.style.display = 'none';
    }

    setBannerHtml(html: string) {
        getRenderer().setBannerHtml(this.container, html);
    }

    renderNullBanner() {
        const banner = this.nullBannerData;
        if (banner instanceof BannerDataNull) {
            this.showBannerDivNull(banner);
        } else if (banner instanceof BannerDataIframeNull) {
            this.showBannerDivIframeNull(banner)
        }

        this.container.classList.toggle("banner_new__loaded", true);

        invokeTrgGc();

        if (this.options.onRender) {
            this.options.onRender(banner);
        }
    }

    renderBanner(banner: IBannerData) {
        let nullBanner = false;
        if (banner instanceof BannerDataNull) {
            this.showBannerDivNull(banner);
            nullBanner = true;
        } else if (banner instanceof BannerDataImage) {
            this.showBannerDivImage(banner);
        } else if (banner instanceof BannerDataText) {
            this.showBannerDivText(banner);
        } else if (banner instanceof BannerDataIframeNull) {
            this.showBannerDivIframeNull(banner);
            nullBanner = true;
        } else if (banner instanceof BannerDataIframe) {
            this.showBannerDivIframe(banner);
        }

        this.container.classList.toggle("banner_new__loaded", true);

        invokeTrgGc();

        if (this.options.onRender) {
            this.options.onRender(banner);
        }

        if (this.options.onError && nullBanner) {
            this.options.onError(banner);
        }
    }

    showBannerDivNull(empty: BannerDataNull) {
        this.nullBannerRenderer.showBannerDiv(empty);
    }

    showBannerDivText(text: BannerDataText) {
        this.textBannerRenderer.showBannerDiv(text);
    }

    showBannerDivImage(image: BannerDataImage) {
        this.imageBannerRenderer.showBannerDiv(image);
    }

    showBannerDivIframeNull(empty: BannerDataIframeNull) {
        this.iframeNullBannerRenderer.showBannerDiv(empty);
    }

    showBannerDivIframe(iframe: BannerDataIframe) {
        this.iframeBannerRenderer.showBannerDiv(iframe);
    }

    encodeClickParam(): string {
        return CLICK_PARAM_PREFIX + this.uid;
    }

    /**
     * Создаем MutationObserver, который следит за изменениеми DOM-а внутри баннера.
     * Довольно грубый, но рабочий способ определить то, что баннер был обновлен своим внутренним скриптом
     */
    createBannerDomObserver(elem: HTMLElement) {
        if (window.MutationObserver) {
            const watcher = this.heightChangeWatcher;
            const observer = new MutationObserver((mutations) => {
                for (let i = 0; i < mutations.length; ++i) {
                    const mutation = mutations[i];
                    if (mutation.addedNodes.length > 0) {
                        watcher.start();
                    }
                }
            });

            observer.observe(elem, {
                childList: true
            });
            this.contentChangeObserver = observer;
        }
    }

    destroyBannerDomObserver() {
        const observer = this.contentChangeObserver;
        if (observer) {
            observer.disconnect();
        }
    }

    removeOldIframe(newIframeId: string) {
        const iframeList = this.container.getElementsByTagName("iframe");
        for (let i = 0; i < iframeList.length; i++) {
            const iframe = iframeList.item(i)!;
            const frameId: string = iframe.id;
            if (newIframeId !== frameId) {
                const parentNode = iframe.parentNode;
                parentNode?.removeChild(iframe);
            }
        }
    }
}

// ======================================================
// ================= BannerRendererJs ===================
// ======================================================

class BannerRendererJs {
    bannerView: BannerViewJs;

    constructor(bannerView: BannerViewJs) {
        this.bannerView = bannerView;
    }

    showBannerDiv(banner: IBannerData) {

    }
}

// ======================================================
// =============== NullBannerRendererJs =================
// ======================================================

class NullBannerRendererJs extends BannerRendererJs {
    showBannerDiv(banner: BannerDataNull) {
        this.bannerView.hideContainer();
        this.bannerView.container.innerHTML = '';
    }
}

// ======================================================
// =============== TextBannerRendererJs =================
// ======================================================

class TextBannerRendererJs extends BannerRendererJs {
    showBannerDiv(banner: BannerDataText) {
        this.bannerView.showContainer();
        this.bannerView.setBannerHtml(banner.text);
    }
}

// ======================================================
// ============ ImageBannerRendererImplJs ===============
// ======================================================

class ImageBannerRendererImplJs extends BannerRendererJs {
    showBannerDiv(banner: BannerDataImage) {
        this.bannerView.showContainer();
        this.bannerView.setBannerHtml(
            this.renderHtml(banner, this.bannerView.encodeClickParam())
        );
        // todo: move this call to view->banner->loader
        if (banner !== null && banner.impressionUrls !== null && banner.impressionUrls.length) {
            setTimeout(function () {
                banner.impressionUrls.forEach(function (impressionUrl) {
                    const img = new Image();
                    img.src = impressionUrl;
                });
            } ,10);
        }
    }

    renderHtml(image: BannerDataImage, bannerClickParam: string): string {
        return  "<div class=\"rb_div\">" +
                    "<div class=\"rb_banner\">" +
                        "<a href=\"" + image.targetUrl + "\" onclick=\"rb_js_bannerClick('" + bannerClickParam + "');return false;\">" +
                            "<img src=\"" + image.url + "\" width=\"" + image.width + "\" height=\"" + image.height + "\"" +
                            " border=\"0\" alt=\"" + image.title + "\" title=\"" + image.title + "\"/>" +
                        "</a>" +
                    "</div>" +
                "</div>";
    }
}

// ======================================================
// =============== IframeNullBannerRendererJs =================
// ======================================================

class IframeNullBannerRendererJs extends BannerRendererJs {
    showBannerDiv(banner: BannerDataNull) {
        this.bannerView.removeOldIframe('');
        this.bannerView.hideContainer();
        this.bannerView.container.innerHTML = '';
    }
}

// ======================================================
// =============== IframeBannerRendererJs =================
// ======================================================

class IframeBannerRendererJs extends BannerRendererJs {
    showBannerDiv(banner: BannerDataIframe) {
        this.bannerView.removeOldIframe(banner.id);
        this.bannerView.showContainer();
    }
}

// ======================================================
// ============== HeightChangeWatcherJs =================
// ======================================================

const POLL_TIMEOUT_MS = 100;
const MAX_POLLS = 15;

class HeightChangeWatcherJs {
    bannerView: BannerViewJs;
    running = false;
    height = 0;
    count = 0;
    heightChangeWatcher = 0;

    constructor(bannerView: BannerViewJs) {
        this.bannerView = bannerView;
    }

    start() {
        if (this.running) {
            return;
        }

        const container: HTMLElement = this.bannerView.container;

        this.count = 0;
        this.running = true;
        this.height = container.offsetHeight;

        this.heightChangeWatcher = setInterval(() => {
            const newHeight = container.offsetHeight;
            if (this.count < MAX_POLLS) {
                if (newHeight > 0 && newHeight !== this.height) {
                    this.height = newHeight;
                    DispatcherJs.get().bannerViewStateChangedEvent.trigger();
                }
                this.count++;
            } else {
                this.stop();
            }
        }, POLL_TIMEOUT_MS);
    }

    stop() {
        if (this.running) {
            clearInterval(this.heightChangeWatcher);
            this.running = false;
        }
    }
}

// ======================================================
// ============== Глобальные переменные =================
// ======================================================

window.rb_js_bannerClick = handleBannerClick;

export {BannerViewJs};